import { memo, FC, useRef, useState, useEffect, useCallback } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Center, Float, useGLTF } from "@react-three/drei";
import { PostProcessing } from "./post-processing.tsx";
import { EnvironmentWrapper } from "./environment.tsx";
import * as THREE from "three";
import style from "./style.module.scss";
import { ArrowTop } from "../../assets/icon/index.jsx";
import TermaLogo from "../../assets/img/terma-logo.png";
import AlterTextLogo from "../../assets/img/logo/type-logo-black.png";

const modelPath = "/macintosh.glb";
useGLTF.preload(modelPath);

function CameraAnimator({
  cameraRef,
  isStart,
}: {
  cameraRef: React.MutableRefObject<THREE.Camera | null>;
  isStart: boolean;
}) {
  const startTime = useRef<number | null>(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  useFrame(() => {
    if (!cameraRef.current || !isStart || hasPlayed) return;

    if (startTime.current === null) startTime.current = performance.now();

    const elapsed = (performance.now() - startTime.current) / 2000;
    const t = Math.min(elapsed, 1);
    const ease = t * t * (3 - 2 * t);

    cameraRef.current.position.y = THREE.MathUtils.lerp(-20, -2, ease);

    if (t >= 1) {
      setHasPlayed(true);
      startTime.current = null;
    }
  });

  return null;
}

export default function App(): JSX.Element {
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.Camera | null>(null);
  const [modelScale, setModelScale] = useState(3);
  const [isStart, setStart] = useState(false);

  const bgColor = "transparent";
  const envIntensity = 0.3;
  const highlight = "#F4DFBE";

  const postProcessParams = {
    bloom1: { enabled: false },
    dithering: {
      effectResolution: 4,
      pixelation: 2,
      grayscale: true,
      tintColor: "#F4DFBE",
    },
  };

  useEffect(() => {
    if (rendererRef.current)
      rendererRef.current.setClearColor(new THREE.Color(bgColor));
  }, [bgColor]);

  const handleResize = useCallback(() => {
    const isSmallScreen = window.innerWidth <= 768;
    setModelScale(isSmallScreen ? 2.4 : 3);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  return (
    <div className={`${style.wrapper} ${isStart ? style.start : ""}`}>
      <div onClick={() => setStart(true)} className={style.logoWrapper}>
        <img src={TermaLogo} alt="terma-logo" />
        <span>LET'S START</span>
      </div>

      <div className={style.text}>
        <h1>2x FASTER API</h1>
        <p>
          TERMA is a command-line–inspired productivity platform that merges the
          power of terminal commands with modern UI.
        </p>
      </div>

      <div className={style.buttonWrapper}>
        <button>
          JOIN WAITLIST <ArrowTop />
        </button>
      </div>

      <a className={style.altertext} href="/studio">
        <span>by</span> <img src={AlterTextLogo} alt="alter-text logo" />
      </a>

      <div className={style.canvasWrapper}>
        <Canvas
          shadows
          camera={{ position: [0, -20, 4], fov: 65 }}
          gl={{ alpha: true, premultipliedAlpha: false }}
          onCreated={({ gl, camera }) => {
            rendererRef.current = gl;
            gl.setClearColor(0x000000, 0);
            cameraRef.current = camera;
          }}
        >
          <CameraAnimator cameraRef={cameraRef} isStart={isStart} />

          <group position={[0, -1, 0]}>
            <Float floatIntensity={2} rotationIntensity={1} speed={2}>
              <Center
                scale={modelScale}
                position={[0, 0.8, 0]}
                rotation={[0, -Math.PI / 3.5, -0.4]}
              >
                <Macintosh />
              </Center>
            </Float>
          </group>

          <OrbitControls enableZoom={false} />
          <EnvironmentWrapper intensity={envIntensity} highlight={highlight} />
          <PostProcessing params={postProcessParams} />
        </Canvas>
      </div>
    </div>
  );
}

function Macintosh(props: any): JSX.Element {
  const { scene } = useGLTF("/macintosh.glb") as any;
  useFrame(() => {
    if (scene) scene.rotation.y -= 0.002;
  });

  return (
    <primitive
      object={scene}
      scale={0.6}
      position={[0, -0.1, 0]}
      rotation={[0, Math.PI / 2, 0]}
      {...props}
    />
  );
}
